<template>
  <a-form layout="vertical" class="form" ref="formRef" :rules="rules" :model="formState">
    <h3>Completa la configuración inicial</h3>

    <a-form-item label="Nombre de plantilla" class="name-input" name="name" required>
      <a-input v-model:value="formState.name" placeholder="Escribir nombre de la plantilla" />
    </a-form-item>

    <h4>Selecciona las campañas y colas que deseas asociar a tu plantilla</h4>

    <CampaignPicker
      v-model:value="formState.portfolios"
      withName
      withOutEntity
      multiple
      :showAll="false"
      label="Campañas asociadas"
    />
    <QueuePicker
      v-model:value="formState.queues"
      label="Colas asociadas"
      multiple
      :showAll="false"
      :withOutPortfolio="false"
      :portfolio="formState.portfolios"
      :name="'queues'"
      class="queues-picker"
    />

    <p class="info">Encontrarás esta plantilla cada vez que selecciones las colas configuradas.</p>
  </a-form>
</template>

<script setup lang="ts">
import { reactive, ref, toRaw, watch, computed, nextTick } from "vue";
import { FormInstance } from "ant-design-vue";
import { cloneDeep, isEqual } from "lodash";

import { ConfigurationFormState } from "@/@types/channels/sms/dto";

import CampaignPicker from "@/app/shared/components/pickers/CampaignPicker.vue";
import QueuePicker from "@/app/shared/components/pickers/QueuePicker.vue";
import { SmsTemplate } from "@/@types/channels/sms";

const props = defineProps<{ smsTemplate?: SmsTemplate }>();

const emit = defineEmits<{ toggleButton: [disabled: boolean] }>();

const formRef = ref<FormInstance>();
const formState: ConfigurationFormState = reactive({
  name: props.smsTemplate?.name || "",
  portfolios:
    [...new Set(props.smsTemplate?.queues.flatMap((q) => q.portfolios.map((p) => p.id)))] || [],
  queues: props.smsTemplate?.queues.map((q) => q.id) || undefined,
});

const rules = computed(() => {
  return {
    name: [{ required: true, message: "Por favor ingresar nombre de plantilla" }],
    queues:
      formState.portfolios.length > 0
        ? [
            {
              required: true,
              message: "Por favor ingresar al menos una cola",
            },
          ]
        : [],
  };
});

const referenceFormState = cloneDeep(toRaw(formState));

watch(formState, async () => {
  nextTick(() => {
    formRef.value
      ?.validate()
      .then(() => {
        emit("toggleButton", isEqual(referenceFormState, formState));
      })
      .catch(() => {
        emit("toggleButton", true);
      });
  });
});

defineExpose({ formState });
</script>

<style scoped lang="sass">
h3
  font-size: 24px
  font-weight: 600
  color: $gray-9

h4
  font-size: 16px
  font-weight: 600
  margin: 20px 0
  color: $gray-9

.info
  color: $gray-7

.ant-form-item.name-input
  margin: 0

:deep(.queues-picker.ant-form-item)
  margin: 0 !important
</style>
